import { useEffect, useState } from 'react';
import type { GeocodingResult } from '@freedom/geospatial';
import type { DesignSite } from '@freedom/shared';
import { Crosshair, MapPin, Search, X } from 'lucide-react';
import { api } from './api';
import { displayEquipmentName } from './radio-parameters';

export function SearchBox({ sites = [], onSelect, onSelectSite }: { sites?: DesignSite[]; onSelect(result: GeocodingResult): void; onSelectSite?(site: DesignSite): void }): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); setError(null); return; }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      api.search(query, controller.signal)
        .then((response) => { setResults(response.results); setError(null); })
        .catch((reason: unknown) => { if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Ошибка поиска'); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 420);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [query]);

  const normalizedQuery = query.trim().toLocaleLowerCase('ru');
  const matchingSites = normalizedQuery.length < 2 ? [] : sites.filter((site) => `${site.name} ${site.equipmentName} ${displayEquipmentName(site.equipmentName)}`.toLocaleLowerCase('ru').includes(normalizedQuery)).slice(0, 20);
  const siteGroups = Object.entries(matchingSites.reduce<Record<string, DesignSite[]>>((groups, site) => {
    const equipmentName = displayEquipmentName(site.equipmentName);
    return { ...groups, [equipmentName]: [...(groups[equipmentName] ?? []), site] };
  }, {}));

  return (
    <div className="search-area" onClick={(event) => event.stopPropagation()}>
      <label className="search-box">
        <Search size={17} />
        <input aria-label="Поиск адреса или координат" placeholder="Адрес или 57.6261, 39.8845" value={query} onChange={(event) => setQuery(event.target.value)} />
        {query ? <button type="button" aria-label="Очистить поиск" onClick={() => setQuery('')}><X size={14} /></button> : <kbd>Ctrl K</kbd>}
      </label>
      {(siteGroups.length > 0 || results.length > 0 || error || loading) ? <div className="search-results grouped-search-results">
        {siteGroups.map(([equipment, matches]) => <section className="search-group" key={equipment}><div className="search-group-title"><span>{equipment}</span><b>{matches.length}</b></div>{matches.map((site) => <button type="button" key={site.id} onClick={() => { onSelectSite?.(site); setQuery(site.name); setResults([]); }}><span><Crosshair size={16} /></span><span><strong>{site.name}</strong><small>{site.type === 'base_station' ? 'Access Point' : site.type === 'client' ? 'Station' : 'Site'} · {site.coordinates.lat.toFixed(5)}, {site.coordinates.lon.toFixed(5)}</small></span></button>)}</section>)}
        {results.length > 0 ? <div className="search-group-title location"><span>Местоположение</span><b>{results.length}</b></div> : null}
        {loading ? <span className="search-status">Поиск…</span> : null}
        {error ? <span className="search-status error">{error}</span> : null}
        {results.map((result) => <button type="button" key={result.id} onClick={() => { onSelect(result); setQuery(result.displayName); setResults([]); }}>
          <span>{result.category === 'coordinates' ? <Crosshair size={16} /> : <MapPin size={16} />}</span><span><strong>{result.displayName}</strong><small>{result.source}</small></span>
        </button>)}
      </div> : null}
    </div>
  );
}
